<script setup lang="ts">
const { t } = useI18n()

// Countdown timer logic
const now = ref(new Date())
const countdown = ref('')
const isAfternoonPickup = ref(false)

// Get shipping cut-off time based on user's local timezone
function getCutoffTime() {
  const userDate = new Date()
  const currentHour = userDate.getHours()

  if (currentHour >= 16) {
    // After 4 PM but before midnight: show countdown to morning pickup
    // Morning cutoff at 8 AM (1 hour before 9 AM pickup)
    userDate.setDate(userDate.getDate() + 1)
    userDate.setHours(8, 0, 0, 0)
    isAfternoonPickup.value = false
  }
  else if (currentHour >= 0 && currentHour < 8) {
    // Midnight to 8 AM: show countdown to afternoon pickup at 4 PM
    userDate.setHours(16, 0, 0, 0)
    isAfternoonPickup.value = true
  }
  else {
    // 8 AM to 4 PM: show countdown to 4 PM afternoon pickup
    userDate.setHours(16, 0, 0, 0)
    isAfternoonPickup.value = true
  }

  return userDate
}

const cutoffTime = ref(getCutoffTime())

// Format time difference as HH:MM:SS
function formatTimeDiff(targetDate: Date, currentDate: Date) {
  const diff = targetDate.getTime() - currentDate.getTime()
  if (diff <= 0)
    return '00:00:00'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

// Update countdown every second for maximum urgency
let timer: NodeJS.Timeout | null = null

onMounted(() => {
  const updateCountdown = () => {
    now.value = new Date()

    // Check if we need to update the cutoff time
    const newCutoff = getCutoffTime()
    if (newCutoff.getTime() !== cutoffTime.value.getTime()) {
      cutoffTime.value = newCutoff
    }

    countdown.value = formatTimeDiff(cutoffTime.value, now.value)
  }

  updateCountdown()
  timer = setInterval(updateCountdown, 1000) // Update every second
})

onBeforeUnmount(() => {
  if (timer)
    clearInterval(timer)
})

const countdownHTML = computed(() => {
  const currentHour = now.value.getHours()

  // Use different message keys based on time of day
  if (currentHour >= 16) {
    // After 4 PM: message about morning shipping
    return t('product.order_countdown.morning_message', [
      `<span class="relative inline-block animate-pulse text-red-600 font-bold tabular-nums">${countdown.value}</span>`,
    ])
  }
  else {
    // Regular message for same-day shipping
    return t('product.order_countdown.message', [
      `<span class="relative inline-block animate-pulse text-red-600 font-bold tabular-nums">${countdown.value}</span>`,
    ])
  }
})
</script>

<template>
  <div class="mx-auto mb2 w-full flex items-center">
    <div class="w-full flex items-center rd-md bg-amber-100 py1 text-center">
      <div class="w-full flex items-center justify-center px-2">
        <p class="text-xs text-amber-700 font-semibold">
          <LazyUiTooltipProvider>
            <LazyUiTooltip>
              <LazyUiTooltipTrigger>
                <span class="cursor-pointer text-center" v-html="countdownHTML" />
              </LazyUiTooltipTrigger>
              <LazyUiTooltipContent>
                <div class="max-w-xs">
                  {{ t('product.order_countdown.tooltip') }}
                </div>
              </LazyUiTooltipContent>
            </LazyUiTooltip>
          </LazyUiTooltipProvider>
        </p>
      </div>
    </div>
  </div>
</template>
